// src/utils/identiteDeclarant.ts — SCE Issues
// Les identités sous lesquelles une même personne peut apparaître comme déclarant.
//
// ─── Pourquoi ce n'est pas simplement l'UPN ───
//
// `Issue.declarant` vient de la colonne système Author, dont Graph renvoie
// l'ADRESSE E-MAIL. Pour un compte Sartorius interne elle coïncide avec l'UPN,
// d'où le raccourci historique « declarant === upn » — longtemps invisible
// parce que tous les utilisateurs étaient internes.
//
// Un guest B2B casse ce raccourci. Son UPN est de la forme
//   jean.martin_vendor.com#EXT#@sartorius.onmicrosoft.com
// alors qu'Author porte son e-mail externe réel (jean.martin@vendor.com).
// Les deux ne coïncident JAMAIS : comparer au seul UPN renvoyait une liste
// vide à tout sous-traitant, y compris sur la déclaration qu'il venait de
// créer — symptôme le plus déroutant possible pour un nouvel utilisateur.
//
// La règle vit donc ici, et nulle part ailleurs : quatre endroits la
// consommaient en la réécrivant, deux l'avaient écrite juste.

/**
 * Le strict nécessaire pour identifier quelqu'un.
 *
 * Volontairement structurel plutôt que `CurrentUser` : un util ne doit pas
 * dépendre d'un hook, et ça garde la fonction testable sans React.
 */
export interface IdentitesSource {
  upn: string
  email: string
}

/**
 * Reconstitue l'e-mail externe d'un guest B2B depuis son UPN.
 *
 * Filet de sécurité : on tient normalement l'e-mail de la claim `email` du
 * jeton, mais rien ne garantit sa présence — elle dépend de la façon dont le
 * compte invité a été créé. Sans elle, le sous-traitant ne retrouverait aucune
 * de ses déclarations, et le symptôme (« l'app est vide ») ne désignerait pas
 * sa cause.
 *
 * Azure AD construit l'UPN d'un invité de façon déterministe :
 *   jean.martin@vendor.com
 *     → jean.martin_vendor.com#EXT#@sartorius.onmicrosoft.com
 * Le DERNIER `_` avant `#EXT#` sépare la partie locale du domaine — d'où le
 * `lastIndexOf`, un e-mail pouvant lui-même contenir des underscores.
 *
 * @returns l'e-mail d'origine, ou null si l'UPN n'est pas celui d'un invité
 */
export function emailDepuisUpnGuest(upn: string): string | null {
  if (!upn) return null
  const marqueur = upn.toUpperCase().indexOf('#EXT#')
  if (marqueur === -1) return null

  const source = upn.slice(0, marqueur)
  const separateur = source.lastIndexOf('_')
  if (separateur <= 0 || separateur === source.length - 1) return null

  return `${source.slice(0, separateur)}@${source.slice(separateur + 1)}`
}

/**
 * Les identités normalisées d'un utilisateur, dédoublonnées.
 *
 * Pour un interne, UPN et e-mail sont identiques → un seul élément.
 * Pour un guest B2B → son UPN, sa claim `email`, et l'e-mail reconstitué
 * depuis l'UPN si la claim venait à manquer.
 */
export function identitesDeclarant(user: IdentitesSource): string[] {
  return Array.from(
    new Set(
      [user.upn, user.email, emailDepuisUpnGuest(user.upn)]
        .filter((v): v is string => Boolean(v))
        .map((v) => v.trim().toLowerCase()),
    ),
  )
}

/**
 * Le déclarant d'un problème fait-il partie de ces identités ?
 *
 * @param declarant valeur brute d'`Issue.declarant` (peut être vide si Graph
 *                  n'a pas résolu l'auteur — on répond alors `false` plutôt
 *                  que de rattacher la fiche à tout le monde)
 */
export function estDeclarePar(
  declarant: string | undefined,
  identites: readonly string[],
): boolean {
  if (!declarant) return false
  return identites.includes(declarant.trim().toLowerCase())
}
